import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as api from '../services/apiClient';
import { InfoTooltip } from './InfoTooltip';
import { useImpersonation } from '../context/ImpersonationContext';

interface FileUploadInputProps {
  id: string;
  label: string;
  accept: string;
  required?: boolean;
  existingFileKey?: string | null;
  existingFileName?: string | null;
  onChange: (fileKey: string | null, file: File | null) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
  onReplaceModeChange?: (isReplacing: boolean) => void;
  disabled?: boolean;
  infoContent?: React.ReactNode;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

// Maximum file size in bytes (10MB)
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Sanitizes a filename to prevent path traversal and unsafe characters.
 * Preserves Unicode characters (accented letters, non-Latin scripts).
 * Returns a safe filename or a fallback if the result would be empty.
 */
const sanitizeFilename = (filename: string): string => {
  // Extract extension from original filename
  const lastDotIndex = filename.lastIndexOf('.');
  const originalExtension = lastDotIndex > 0 ? filename.slice(lastDotIndex) : '';
  const nameWithoutExt = lastDotIndex > 0 ? filename.slice(0, lastDotIndex) : filename;

  // Remove path traversal sequences and directory separators
  let sanitized = nameWithoutExt
    .replace(/\.\./g, '')
    .replace(/[/\\]/g, '')
    .replace(/[\x00-\x1f\x7f]/g, ''); // Remove control characters

  // Remove only truly unsafe characters while preserving Unicode letters/numbers
  // Keep: Unicode letters (\p{L}), Unicode numbers (\p{N}), marks (\p{M}),
  // dashes, underscores, dots, spaces
  sanitized = sanitized.replace(/[^\p{L}\p{N}\p{M}\-_.\s]/gu, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  // Sanitize the extension as well (remove unsafe chars but keep the dot)
  let sanitizedExtension = originalExtension
    .replace(/[\x00-\x1f\x7f]/g, '')
    .replace(/[/\\]/g, '')
    .replace(/\.\./g, '.');

  // Keep only safe extension characters (alphanumeric and single dot at start)
  if (sanitizedExtension) {
    const extWithoutDot = sanitizedExtension.slice(1).replace(/[^a-zA-Z0-9]/g, '');
    sanitizedExtension = extWithoutDot ? `.${extWithoutDot}` : '';
  }

  // Check if name is empty or consists only of dots/whitespace
  const isEmptyName = !sanitized || /^[\s.]*$/.test(sanitized);

  if (isEmptyName) {
    // Generate fallback with original extension or default
    const fallbackExt = sanitizedExtension || '.docx';
    sanitized = `document_${Date.now()}${fallbackExt}`;
  } else {
    // Reattach the sanitized extension
    sanitized = sanitized + sanitizedExtension;
  }

  // Truncate to 255 chars max
  if (sanitized.length > 255) {
    // Preserve extension when truncating
    const ext = sanitizedExtension || '';
    // Strip extension from sanitized (it was already appended above) to get base name
    const baseName = ext && sanitized.endsWith(ext)
      ? sanitized.slice(0, -ext.length)
      : sanitized;
    const maxNameLength = 255 - ext.length;
    sanitized = baseName.slice(0, maxNameLength) + ext;
  }

  return sanitized;
};

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  id,
  label,
  accept,
  required = false,
  existingFileKey = null,
  existingFileName = null,
  onChange,
  onUploadStateChange,
  onReplaceModeChange,
  disabled = false,
  infoContent,
}) => {
  const { activeAlias } = useImpersonation();
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showReplaceZone, setShowReplaceZone] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const previousAliasRef = useRef(activeAlias);

  // Abort upload if impersonation changes mid-upload
  useEffect(() => {
    if (previousAliasRef.current !== activeAlias) {
      if (abortControllerRef.current && uploadState === 'uploading') {
        abortControllerRef.current.abort();
        setUploadState('idle');
        setProgress(0);
        setFileName(null);
        setErrorMessage(null);
        onChange(null, null);
      }
      previousAliasRef.current = activeAlias;
    }
  }, [activeAlias, uploadState, onChange]);

  // Cleanup on unmount: abort any in-flight upload and mark as unmounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Notify parent of upload state changes
  useEffect(() => {
    onUploadStateChange?.(uploadState === 'uploading');
  }, [uploadState, onUploadStateChange]);

  // Notify parent when entering/exiting replace mode
  useEffect(() => {
    onReplaceModeChange?.(showReplaceZone);
  }, [showReplaceZone, onReplaceModeChange]);

  const handleUpload = useCallback(
    async (file: File) => {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setUploadState('error');
        setErrorMessage('Le fichier est trop volumineux, taille maximale : 10MB');
        return;
      }

      // Validate file type
      const isValidType =
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.toLowerCase().endsWith('.docx');

      if (!isValidType) {
        setUploadState('error');
        setErrorMessage('Seuls les fichiers DOCX sont acceptés');
        return;
      }

      // Cancel any previous upload
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Create new AbortController for this upload
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setUploadState('uploading');
      setProgress(0);
      setFileName(file.name);
      setErrorMessage(null);

      try {
        // Step 1: Get presigned URL
        const contentType =
          file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        // Sanitize the filename to prevent path traversal and unsafe characters
        const safeFilename = sanitizeFilename(file.name);

        const urlResponse = await api.generateUploadUrl({
          filename: safeFilename,
          content_type: contentType,
        });

        // Check if cancelled or unmounted before continuing
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (!urlResponse.success || !urlResponse.data) {
          throw new Error(urlResponse.error || "Erreur lors de la génération de l'URL");
        }

        const { presigned_url, file_key } = urlResponse.data;

        // Step 2: Upload to S3 with progress tracking
        const uploadResult = await api.uploadToS3(
          presigned_url,
          file,
          (percent) => {
            if (isMountedRef.current && !abortController.signal.aborted) {
              setProgress(percent);
            }
          },
          abortController.signal
        );

        // Check if cancelled or unmounted before continuing
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || "Erreur lors de l'upload");
        }

        // Step 3: Finalize upload
        const finalizeResponse = await api.finalizeUpload({
          file_key,
          filename: safeFilename,
          content_type: contentType,
          file_size: file.size,
        });

        // Check if cancelled or unmounted before updating state
        if (abortController.signal.aborted || !isMountedRef.current) {
          return;
        }

        if (!finalizeResponse.success) {
          throw new Error(finalizeResponse.error || 'Erreur lors de la finalisation');
        }

        // Success!
        setUploadState('success');
        setProgress(100);
        setShowReplaceZone(false);
        onChange(file_key, file);
      } catch (error) {
        // Only update error state if still mounted and not cancelled
        if (isMountedRef.current && !abortController.signal.aborted) {
          setUploadState('error');
          setErrorMessage(error instanceof Error ? error.message : 'Erreur inconnue');
          onChange(null, null);
        }
      } finally {
        // Clear the abort controller reference if this is the current one
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [onChange]
  );

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the dropzone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
    },
    [disabled, handleUpload]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleUpload]
  );

  const handleDropZoneClick = useCallback(() => {
    if (!disabled && uploadState !== 'uploading') {
      inputRef.current?.click();
    }
  }, [disabled, uploadState]);

  const handleReplaceClick = useCallback(() => {
    setShowReplaceZone(true);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleDropZoneClick();
      }
    },
    [handleDropZoneClick]
  );

  // Show existing file indicator in edit mode or after successful upload
  const showExistingIndicator = existingFileKey && uploadState === 'idle' && !showReplaceZone;
  const showUploadedIndicator = uploadState === 'success' && !showReplaceZone;

  // Determine dropzone class
  const dropZoneClasses = [
    'file-upload-dropzone',
    isDragging && 'dragging',
    disabled && 'disabled',
    uploadState === 'success' && 'has-file',
    uploadState === 'error' && 'error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="form-field">
      <div className="form-field-label-row">
        <label htmlFor={id} className="form-field-label">
          {label} {required && '*'}
        </label>
        {infoContent && <InfoTooltip>{infoContent}</InfoTooltip>}
      </div>

      {showExistingIndicator && (
        <div className="file-upload-existing">
          <div className="file-upload-existing-info">
            <svg
              className="file-upload-existing-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span>Document cadre existant{existingFileName ? ` : ${existingFileName}` : ''}</span>
          </div>
          <button
            type="button"
            className="file-upload-replace-btn"
            onClick={handleReplaceClick}
            disabled={disabled}
          >
            Remplacer
          </button>
        </div>
      )}

      {showUploadedIndicator && (
        <div className="file-upload-existing">
          <div className="file-upload-existing-info">
            <svg
              className="file-upload-existing-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            <span>{fileName}</span>
          </div>
          <button
            type="button"
            className="file-upload-replace-btn"
            onClick={() => setShowReplaceZone(true)}
            disabled={disabled}
          >
            Remplacer
          </button>
        </div>
      )}

      {(!showExistingIndicator && !showUploadedIndicator) && (
        <>
          <div
            ref={dropZoneRef}
            className={dropZoneClasses}
            onClick={handleDropZoneClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={`Zone de dépôt pour ${label}`}
            onKeyDown={handleKeyDown}
          >
            <input
              ref={inputRef}
              id={id}
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              className="file-upload-input"
              disabled={disabled}
            />

            <svg
              className="file-upload-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>

            <p className="file-upload-text">
              Glissez-déposez votre fichier ici ou <strong>cliquez pour parcourir</strong>
            </p>
            <p className="file-upload-hint">Format accepté : DOCX</p>
          </div>

          {uploadState === 'uploading' && (
            <div className="file-upload-progress">
              <div
                className="file-upload-progress-bar"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Upload de ${fileName}`}
              >
                <div className="file-upload-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="file-upload-progress-text">
                Upload de {fileName}... {progress}%
              </p>
            </div>
          )}

          {uploadState === 'error' && <div className="file-upload-error" role="alert">{errorMessage}</div>}
        </>
      )}
    </div>
  );
};
